const GUARDED = Symbol.for('worldcoo.applePaySessionAbortGuard');

type AbortMethod = ((this: unknown) => void) & { [GUARDED]?: true };

interface ApplePaySessionGlobal {
	// `abort` may be absent: Apple's SDK polyfill, which Adyen loads in every browser, puts it on each instance.
	ApplePaySession?: { prototype: { abort?: AbortMethod } };
}

function isInvalidAccessError(error: unknown): boolean {
	return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'InvalidAccessError';
}

/**
 * Adyen fails a merchant validation with `console.error(e), session.abort(), onError(e)`, and WebKit's `abort()`
 * throws `InvalidAccessError` once the sheet is closed: the throw skips `onError`, so the real failure, which
 * Adyen hands it as `cause`, never reached a report (TEC-3954). Only that throw is swallowed. Idempotent, and a
 * no-op wherever WebKit's native session is not there to patch. docs/notes/portals/donate-checkout.md
 */
export function installApplePaySessionGuard(scope: ApplePaySessionGlobal = globalThis as ApplePaySessionGlobal): void {
	const prototype = scope.ApplePaySession?.prototype;
	const abort = prototype?.abort;
	if (!prototype || typeof abort !== 'function' || abort[GUARDED]) return;

	const guardedAbort: AbortMethod = function (this: unknown) {
		try {
			abort.call(this);
		} catch (error) {
			if (!isInvalidAccessError(error)) throw error;
		}
	};
	guardedAbort[GUARDED] = true;
	prototype.abort = guardedAbort;
}

/** The `AdyenCheckoutError` Adyen's Apple Pay component wraps every Apple Pay session failure in. */
export function isApplePaySessionFailure(message: string): boolean {
	return message.startsWith('ApplePay - ');
}
